import { DeliveryTime, Timespan } from '~api/api';

import { TranslateFn } from '~src/main/context/localization';

type DeliveryTimeAdditionalArguments = {
  showDeliveryTimeAsEstimate?: boolean;
};

export function formatDeliveryTime(
  tICU: TranslateFn,
  deliveryTime: DeliveryTime,
  additional: DeliveryTimeAdditionalArguments = {}
) {
  if (deliveryTime.custom_text) {
    return deliveryTime.custom_text;
  }

  /*
   * It seems like it does not make sense to make promises like "today" in faq
   * widget, but this maybe needs more theoretical workout. Anyway things like
   * "delivery in 0-1 days" does not look good.
   */
  const clampedDeliveryTime: DeliveryTime = {
    ...deliveryTime,
    min: Math.max(deliveryTime.min!, 1),
    max: Math.max(deliveryTime.max!, 1),
  };

  let dateStr = '';

  if (
    clampedDeliveryTime.min === clampedDeliveryTime.max ||
    deliveryTime.time_formatting === 'MAX'
  ) {
    dateStr = formatMaxDeliveryTime(tICU, clampedDeliveryTime);
  } else {
    dateStr = formatIntervalDeliveryTime(tICU, clampedDeliveryTime);
  }

  const timespanStr = formatTimespan(tICU, deliveryTime.timespan);

  const prefix = tICU('widget.delivery_time.DELIVERY_TYPE_DELIVERY');

  const estimateDisclaimer = additional.showDeliveryTimeAsEstimate
    ? tICU('widget.delivery_time.ESTIMATED')
    : '';

  return `${prefix} ${dateStr} ${timespanStr} ${estimateDisclaimer}`.trim();
}

function formatIntervalDeliveryTime(tICU: TranslateFn, deliveryTime: DeliveryTime) {
  const params = {
    min: deliveryTime.min,
    max: deliveryTime.max,
  };

  switch (deliveryTime.unit) {
    case 'business_day':
      return tICU('widget.delivery_time.INTERVAL_BUSINESS_DAY_DAYS', params);
    case 'day':
    default:
      return tICU('widget.delivery_time.INTERVAL_DAY_DAYS', params);
    case 'hour':
      return tICU('widget.delivery_time.INTERVAL_HOUR', params);
    case 'minute':
      return tICU('widget.delivery_time.INTERVAL_MINUTE', params);
    case 'week':
      return tICU('widget.delivery_time.INTERVAL_WEEKS', params);
  }
}

function formatMaxDeliveryTime(tICU: TranslateFn, deliveryTime: DeliveryTime) {
  const params = { max: deliveryTime.max };

  switch (deliveryTime.unit) {
    case 'business_day':
      return tICU('widget.delivery_time.MAX_BUSINESS_DAYS', params);
    case 'day':
    default:
      return tICU('widget.delivery_time.MAX_DAYS', params);
    case 'hour':
      return tICU('widget.delivery_time.MAX_HOUR', params);
    case 'minute':
      return tICU('widget.delivery_time.MAX_MINUTE', params);
    case 'week':
      return tICU('widget.delivery_time.MAX_WEEKS', params);
  }
}

/*
 * This oversimplistic implementation is probably all we need for now.
 */
function formatTimespan(tICU: TranslateFn, timespan?: Timespan) {
  if (!timespan || !timespan.from || !timespan.to) {
    return '';
  }

  const params = {
    min: timespan.from,
    max: timespan.to,
  };

  return tICU('widget.delivery_time.INTERVAL_TIMESPAN', params);
}
